import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faCloudArrowUp } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { crmPostventaApi } from "../crmPostventaApi";
import "./altas-bajas.css";

const PROVINCIAS_LEGAJO = [
  "BUENOS AIRES", "CATAMARCA", "CHACO", "CHUBUT", "CÓRDOBA", "CORRIENTES",
  "ENTRE RÍOS", "FORMOSA", "JUJUY", "LA PAMPA", "LA RIOJA", "MENDOZA",
  "MISIONES", "NEUQUÉN", "RÍO NEGRO", "SALTA", "SAN JUAN", "SAN LUIS",
  "SANTA CRUZ", "SANTA FE", "SANTIAGO DEL ESTERO", "TIERRA DEL FUEGO", "TUCUMÁN",
];

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return "0 B";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getFileExtension = (file) => {
  const name = String(file?.name || "");
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === name.length - 1) {
    return "";
  }
  return name.slice(dotIndex + 1).toLowerCase();
};

const emptyForm = () => ({
  nroLegajo: "",
  terminal: "",
  nombreDeFantasia: "",
  provincia: "",
  tipoPersona: "",
  cuitTitularOSociedad: "",
  razonSocial: "",
  calle: "",
  numero: "",
  pisoDepto: "",
  localidad: "",
  codigoPostal: "",
  emailContacto: "",
  celularWhatsapp: "",
  asesor: "",
});

const mapLegajoToForm = (legajo) => ({
  nroLegajo: legajo?.nroLegajo ?? legajo?.NroLegajo ?? "",
  terminal: legajo?.terminal ?? legajo?.Terminal ?? "",
  nombreDeFantasia: legajo?.nombreDeFantasia ?? legajo?.NombreDeFantasia ?? "",
  provincia: legajo?.provincia ?? legajo?.Provincia ?? "",
  tipoPersona: legajo?.tipoPersona ?? legajo?.TipoPersona ?? "",
  cuitTitularOSociedad: legajo?.cuitTitularOSociedad ?? legajo?.CuitTitularOSociedad ?? "",
  razonSocial: legajo?.razonSocial ?? legajo?.RazonSocial ?? "",
  calle: legajo?.calle ?? legajo?.Calle ?? "",
  numero: legajo?.numero ?? legajo?.Numero ?? "",
  pisoDepto: legajo?.pisoDepto ?? legajo?.PisoDepto ?? "",
  localidad: legajo?.localidad ?? legajo?.Localidad ?? "",
  codigoPostal: legajo?.codigoPostal ?? legajo?.CodigoPostal ?? "",
  emailContacto: legajo?.emailContacto ?? legajo?.EmailContacto ?? "",
  celularWhatsapp: legajo?.celularWhatsapp ?? legajo?.CelularWhatsapp ?? "",
  asesor: legajo?.asesor ?? legajo?.Asesor ?? "",
});

export default function ModificarLegajoPanel({ catalogos, docTypes = [] }) {
  const [search, setSearch] = useState("");
  const [legajos, setLegajos] = useState([]);
  const [loadingLegajos, setLoadingLegajos] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docDraft, setDocDraft] = useState({ nombreDocumento: docTypes[0] || "", file: null });
  const [savingDoc, setSavingDoc] = useState(false);
  const inputRef = useRef(null);

  const provinciaOptions = catalogos?.provincias?.length
    ? catalogos.provincias.map((item) => item.label ?? item.value)
    : PROVINCIAS_LEGAJO;
  const asesorOptions = catalogos?.asesores || [];

  const legajoId = selected?.id ?? selected?.Id ?? null;

  const cargarLegajos = async () => {
    setLoadingLegajos(true);
    try {
      const data = await crmPostventaApi.obtenerLegajos();
      setLegajos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error obteniendo legajos:", error);
      setLegajos([]);
    } finally {
      setLoadingLegajos(false);
    }
  };

  useEffect(() => {
    cargarLegajos();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return legajos;
    return legajos.filter((item) => {
      const nro = String(item?.nroLegajo ?? item?.NroLegajo ?? "").toLowerCase();
      const nombre = String(item?.nombreDeFantasia ?? item?.NombreDeFantasia ?? "").toLowerCase();
      const razon = String(item?.razonSocial ?? item?.RazonSocial ?? "").toLowerCase();
      const cuit = String(item?.cuitTitularOSociedad ?? item?.CuitTitularOSociedad ?? "").toLowerCase();
      return nro.includes(term) || nombre.includes(term) || razon.includes(term) || cuit.includes(term);
    });
  }, [legajos, search]);

  const cargarDocs = async (id) => {
    setLoadingDocs(true);
    try {
      const data = await crmPostventaApi.listarDocumentosLegajo(id);
      setDocs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error obteniendo documentos del legajo:", error);
      setDocs([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleSelect = (legajo) => {
    setSelected(legajo);
    setForm(mapLegajoToForm(legajo));
    setDocDraft({ nombreDocumento: docTypes[0] || "", file: null });
    const id = legajo?.id ?? legajo?.Id;
    if (id) {
      cargarDocs(id);
    } else {
      setDocs([]);
    }
  };

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!legajoId) return;

    setSaving(true);
    try {
      await crmPostventaApi.editarLegajo({
        Id: legajoId,
        NroLegajo: form.nroLegajo === "" ? undefined : Number(form.nroLegajo),
        Terminal: form.terminal || undefined,
        NombreDeFantasia: form.nombreDeFantasia || undefined,
        Provincia: form.provincia || undefined,
        TipoPersona: form.tipoPersona || undefined,
        CuitTitularOSociedad: form.cuitTitularOSociedad || undefined,
        RazonSocial: form.razonSocial || undefined,
        Calle: form.calle || undefined,
        Numero: form.numero || undefined,
        PisoDepto: form.pisoDepto || undefined,
        Localidad: form.localidad || undefined,
        CodigoPostal: form.codigoPostal || undefined,
        EmailContacto: form.emailContacto || undefined,
        CelularWhatsapp: form.celularWhatsapp || undefined,
        Asesor: form.asesor || undefined,
      });

      await cargarLegajos();
      Swal.fire({ title: "Legajo", text: "Los cambios se guardaron correctamente.", icon: "success", timer: 2500 });
    } catch (error) {
      console.error("Error editando legajo:", error);
      Swal.fire({ title: "Legajo", text: error.message || "No se pudo guardar el legajo.", icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitDoc = async (event) => {
    event.preventDefault();
    if (!legajoId || !docDraft.file) return;

    setSavingDoc(true);
    try {
      const archivoBase64 = await fileToDataUrl(docDraft.file);
      await crmPostventaApi.subirDocumentoLegajo({
        IdLegajo: legajoId,
        NombreDocumento: docDraft.nombreDocumento,
        DocumentoTipo: getFileExtension(docDraft.file) || null,
        ArchivoBase64: archivoBase64,
      });

      setDocDraft({ nombreDocumento: docTypes[0] || "", file: null });
      await cargarDocs(legajoId);
      Swal.fire({ title: "Documentos", text: "Archivo cargado correctamente.", icon: "success", timer: 2500 });
    } catch (error) {
      console.error("Error subiendo documento:", error);
      Swal.fire({ title: "Documentos", text: error.message || "No se pudo subir el archivo.", icon: "error" });
    } finally {
      setSavingDoc(false);
    }
  };

  const handleDeleteDoc = async (idDocumento) => {
    if (!idDocumento) return;
    try {
      await crmPostventaApi.eliminarDocumentoLegajo(idDocumento);
      if (legajoId) await cargarDocs(legajoId);
    } catch (error) {
      console.error("Error eliminando documento:", error);
      Swal.fire({ title: "Documentos", text: error.message || "No se pudo eliminar el archivo.", icon: "error" });
    }
  };

  return (
    <div className="crm-altas-bajas-page__stack">
      <section className="crm-altas-bajas-page__card crm-altas-bajas-page__card--baja-search">
        <div className="crm-altas-bajas-page__search-card-header">
          <h3>Modificar legajo</h3>
        </div>

        <div className="crm-altas-bajas-page__search-box">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <Form.Control
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por N.º de legajo, nombre de fantasía, razón social o CUIT..."
          />
        </div>

        <div className="crm-altas-bajas-page__search-results">
          {loadingLegajos ? (
            <div className="crm-altas-bajas-page__search-status">Cargando legajos...</div>
          ) : filtered.length ? (
            filtered.slice(0, 8).map((item) => {
              const id = item?.id ?? item?.Id;
              const isActive = id === legajoId;
              return (
                <button
                  type="button"
                  key={id}
                  className="crm-altas-bajas-page__search-result"
                  style={isActive ? { borderColor: "#2d3035" } : undefined}
                  onClick={() => handleSelect(item)}
                >
                  <strong>
                    {item?.nroLegajo ?? item?.NroLegajo} - {item?.nombreDeFantasia ?? item?.NombreDeFantasia}
                  </strong>
                  <span>
                    {(item?.razonSocial ?? item?.RazonSocial) || "Sin razón social"} · CUIT{" "}
                    {(item?.cuitTitularOSociedad ?? item?.CuitTitularOSociedad) || "-"}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="crm-altas-bajas-page__search-status">No se encontraron legajos.</div>
          )}
        </div>
      </section>

      {selected ? (
        <section className="crm-altas-bajas-page__card crm-altas-bajas-page__card--step-two">
          <div className="crm-altas-bajas-page__step-stack">
            <div className="crm-altas-bajas-page__step-heading">
              <div>
                <small>Legajo {form.nroLegajo}</small>
                <h3>{form.nombreDeFantasia || "Editar legajo"}</h3>
              </div>
            </div>

            <Form onSubmit={handleSubmit} className="crm-altas-bajas-page__step-stack">
              <div className="crm-altas-bajas-page__form-grid">
                <Form.Group>
                  <Form.Label>N.º de legajo</Form.Label>
                  <Form.Control value={form.nroLegajo} onChange={(event) => handleChange("nroLegajo", event.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Terminal</Form.Label>
                  <Form.Control value={form.terminal} onChange={(event) => handleChange("terminal", event.target.value)} />
                </Form.Group>
                <Form.Group className="crm-altas-bajas-page__span-2">
                  <Form.Label>Nombre de fantasía</Form.Label>
                  <Form.Control value={form.nombreDeFantasia} onChange={(event) => handleChange("nombreDeFantasia", event.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Tipo de persona</Form.Label>
                  <Form.Select value={form.tipoPersona} onChange={(event) => handleChange("tipoPersona", event.target.value)}>
                    <option value="">Seleccione</option>
                    <option value="Persona Física">Persona Física</option>
                    <option value="Persona Jurídica">Persona Jurídica</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>CUIT titular / sociedad</Form.Label>
                  <Form.Control value={form.cuitTitularOSociedad} onChange={(event) => handleChange("cuitTitularOSociedad", event.target.value)} />
                </Form.Group>
                <Form.Group className="crm-altas-bajas-page__span-2">
                  <Form.Label>Razón social</Form.Label>
                  <Form.Control value={form.razonSocial} onChange={(event) => handleChange("razonSocial", event.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Provincia</Form.Label>
                  <Form.Select value={form.provincia} onChange={(event) => handleChange("provincia", event.target.value)}>
                    <option value="">Seleccione</option>
                    {provinciaOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Localidad</Form.Label>
                  <Form.Control value={form.localidad} onChange={(event) => handleChange("localidad", event.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Dirección</Form.Label>
                  <Form.Control value={form.calle} onChange={(event) => handleChange("calle", event.target.value)} />
                </Form.Group>
                <div className="crm-altas-bajas-page__triple-grid crm-altas-bajas-page__span-2">
                  <Form.Group>
                    <Form.Label>Número</Form.Label>
                    <Form.Control value={form.numero} onChange={(event) => handleChange("numero", event.target.value)} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Piso / Depto</Form.Label>
                    <Form.Control value={form.pisoDepto} onChange={(event) => handleChange("pisoDepto", event.target.value)} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Código postal</Form.Label>
                    <Form.Control value={form.codigoPostal} onChange={(event) => handleChange("codigoPostal", event.target.value)} />
                  </Form.Group>
                </div>
                <Form.Group>
                  <Form.Label>Email de contacto</Form.Label>
                  <Form.Control type="email" value={form.emailContacto} onChange={(event) => handleChange("emailContacto", event.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Celular / WhatsApp</Form.Label>
                  <Form.Control value={form.celularWhatsapp} onChange={(event) => handleChange("celularWhatsapp", event.target.value)} />
                </Form.Group>
                <Form.Group className="crm-altas-bajas-page__span-2">
                  <Form.Label>Asesor / Gestor</Form.Label>
                  {asesorOptions.length ? (
                    <Form.Select value={form.asesor} onChange={(event) => handleChange("asesor", event.target.value)}>
                      <option value="">Seleccione</option>
                      {asesorOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control value={form.asesor} onChange={(event) => handleChange("asesor", event.target.value)} />
                  )}
                </Form.Group>
              </div>

              <div className="crm-altas-bajas-page__footer-row">
                <div className="crm-altas-bajas-page__helper-chip">* Los cambios se aplican al legajo existente.</div>
                <div className="crm-altas-bajas-page__button-row">
                  <button type="submit" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary" disabled={saving}>
                    {saving ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </div>
            </Form>

            <section className="crm-altas-bajas-page__docs-section">
              <div className="crm-altas-bajas-page__docs-header">
                <div>
                  <h4>Documentos del legajo</h4>
                  <p>* Los documentos existentes se listan abajo.</p>
                </div>
              </div>

              <Form onSubmit={handleSubmitDoc} className="crm-altas-bajas-page__docs-grid">
                <Form.Group>
                  <Form.Label>Tipo de documento</Form.Label>
                  <Form.Select
                    value={docDraft.nombreDocumento}
                    onChange={(event) => setDocDraft((prev) => ({ ...prev, nombreDocumento: event.target.value }))}
                  >
                    {docTypes.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <div className="crm-altas-bajas-page__upload-column">
                  <Form.Label>Archivo</Form.Label>
                  <input
                    ref={inputRef}
                    type="file"
                    hidden
                    onChange={(event) =>
                      setDocDraft((prev) => ({
                        ...prev,
                        file: event.target.files?.[0] || null,
                      }))
                    }
                  />
                  <button type="button" className="crm-altas-bajas-page__upload-box" onClick={() => inputRef.current?.click()}>
                    <FontAwesomeIcon icon={faCloudArrowUp} />
                    <strong>Seleccionar archivo</strong>
                    <span>Peso máximo según validación vigente</span>
                  </button>
                  {docDraft.file ? <div className="crm-altas-bajas-page__selected-file">{docDraft.file.name}</div> : null}
                </div>
                <div className="crm-altas-bajas-page__button-row crm-altas-bajas-page__span-2">
                  <button type="submit" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary" disabled={savingDoc || !docDraft.file}>
                    {savingDoc ? "Guardando..." : "Guardar archivo"}
                  </button>
                </div>
              </Form>

              <div className="crm-altas-bajas-page__docs-table-wrap">
                <table className="crm-altas-bajas-page__docs-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Documento</th>
                      <th>Tipo</th>
                      <th>Tamaño</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingDocs ? (
                      <tr>
                        <td colSpan={5} className="crm-altas-bajas-page__docs-empty">
                          Cargando documentos...
                        </td>
                      </tr>
                    ) : docs.length ? (
                      docs.map((item) => (
                        <tr key={item.idDocumento ?? item.IdDocumento}>
                          <td>{item.idDocumento ?? item.IdDocumento}</td>
                          <td>{item.nombreDocumento ?? item.NombreDocumento}</td>
                          <td>{item.documentoTipo ?? item.DocumentoTipo ?? "-"}</td>
                          <td>{formatFileSize(item.bytes ?? item.Bytes ?? 0)}</td>
                          <td>
                            <button
                              type="button"
                              className="crm-altas-bajas-page__table-link"
                              onClick={() => handleDeleteDoc(item.idDocumento ?? item.IdDocumento)}
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="crm-altas-bajas-page__docs-empty">
                          No hay documentos cargados todavía.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </section>
      ) : null}
    </div>
  );
}
